import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  Box,
  Typography,
  IconButton,
  Button,
  Divider,
} from '@mui/material';
import {
  Close,
  Print,
  Delete,
  Checkroom,
  Backpack,
} from '@mui/icons-material';
import { Pasajero } from './GroupPassengersSection';
import { printPassengerFile } from './GroupPrintTemplate';

interface PassengerFileModalProps {
  open: boolean;
  onClose: () => void;
  pasajero: Pasajero | null;
  onDelete?: (pasajero: Pasajero) => void;
  onPrintFicha?: (pasajero: Pasajero) => void;
  onPrintContratos?: (pasajero: Pasajero) => void;
  currentTrip?: string;
  currentYear?: string;
  packageType?: string;
}

const PassengerFileModal: React.FC<PassengerFileModalProps> = ({
  open,
  onClose,
  pasajero,
  onDelete,
  onPrintFicha,
  onPrintContratos,
  currentTrip = "15 FOREVER",
  currentYear = "2028",
  packageType = "QUINCE"
}) => {
  if (!pasajero) return null;

  // Formatting name
  const nameParts = (pasajero.nombre || "").split(',');
  const apellido = pasajero.apellido || nameParts[0]?.trim() || '';
  const nombre = pasajero.nombre_solamente || nameParts[1]?.trim() || '';

  const PINK_THEME = '#e73c8d'; // Hot Pink / Magenta matching the images
  const VALUE_BG = '#f5f1ea'; // Light beige for labels as seen in image
  const BORDER_SOFT = '#f8bbd0';

  const InfoRow = ({ label, value }: { label: string; value: any }) => (
    <Box sx={{ display: 'flex', mb: 0.8, alignItems: 'stretch' }}>
      <Box sx={{ 
        width: 170, 
        bgcolor: VALUE_BG, 
        px: 1, 
        py: 0.5,
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'flex-end',
        mr: 1
      }}>
        <Typography variant="caption" sx={{ fontWeight: 800, color: '#333', textAlign: 'right', textTransform: 'uppercase', fontSize: '0.65rem' }}>
          {label}
        </Typography>
      </Box>
      <Box sx={{ 
        flex: 1, 
        bgcolor: 'white', 
        border: '1px solid #eee',
        px: 1.5, 
        py: 0.5,
        display: 'flex', 
        alignItems: 'center'
      }}>
        <Typography variant="body2" sx={{ fontWeight: 600, color: '#333', fontSize: '0.8rem' }}>
          {typeof value === 'boolean' ? (value ? 'SÍ' : 'NO') : (value || '-')}
        </Typography>
      </Box>
    </Box>
  );

  const SectionTitle = ({ title }: { title: string }) => (
    <Typography variant="h6" sx={{ 
      color: PINK_THEME, 
      fontWeight: 900, 
      mt: 2.5, 
      mb: 1.2,
      fontSize: '0.9rem',
      letterSpacing: 1.2,
      textTransform: 'uppercase'
    }}>
      {title}
    </Typography>
  );

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="lg" 
      fullWidth 
      PaperProps={{ sx: { borderRadius: 1, overflow: 'hidden' } }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        bgcolor: 'white', 
        px: 3, 
        py: 1
      }}>
        <Typography sx={{ fontWeight: 600, color: '#666', fontSize: '0.9rem' }}>Ficha de Pasajero</Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Button 
            startIcon={<Print fontSize="small" />} 
            variant="contained" 
            size="small" 
            sx={{ 
              bgcolor: PINK_THEME, 
              '&:hover': { bgcolor: '#c2185b' },
              textTransform: 'none',
              borderRadius: 1,
              fontSize: '0.75rem',
              py: 0.5
            }} 
            onClick={() => printPassengerFile(pasajero, currentTrip, currentYear, packageType)}
          >
            Imprimir Ficha
          </Button>
          <Button 
            startIcon={<Print fontSize="small" />} 
            variant="contained" 
            size="small" 
            sx={{ 
              bgcolor: PINK_THEME, 
              '&:hover': { bgcolor: '#c2185b' },
              textTransform: 'none',
              borderRadius: 1,
              fontSize: '0.75rem',
              py: 0.5
            }} 
            onClick={() => onPrintContratos && onPrintContratos(pasajero)}
          >
            Imprimir Contratos
          </Button>
          <Button 
            startIcon={<Delete fontSize="small" />} 
            variant="contained" 
            size="small" 
            sx={{ 
              bgcolor: '#d32f2f', 
              '&:hover': { bgcolor: '#b71c1c' },
              textTransform: 'none',
              borderRadius: 1,
              fontSize: '0.75rem',
              py: 0.5
            }} 
            onClick={() => onDelete && onDelete(pasajero)}
          >
            Eliminar
          </Button>
          <IconButton onClick={onClose} size="small" sx={{ ml: 1 }}>
            <Close fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent sx={{ bgcolor: 'white', p: 4, pt: 0.5 }}>
        <Box sx={{ 
          border: `2px solid ${PINK_THEME}`, 
          m: 1,
          p: 0.5,
          borderRadius: 0.5
        }}>
          <Box sx={{ 
            border: `1px solid ${PINK_THEME}`, 
            p: 4, 
            bgcolor: 'white'
          }}>
            
            {/* Header section with Logo and Headline */}
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Box sx={{ position: 'relative', display: 'inline-block', mb: 3 }}>
                <Typography variant="h3" sx={{ 
                  fontWeight: 900, 
                  color: PINK_THEME, 
                  letterSpacing: 6, 
                  lineHeight: 0.9,
                  mb: 0.5
                }}>
                  YO VOY
                </Typography>
                <Typography variant="caption" sx={{ 
                  color: PINK_THEME, 
                  fontWeight: 800, 
                  letterSpacing: 5,
                  display: 'block',
                  mt: 0.5,
                  fontSize: '0.7rem'
                }}>
                  {packageType.toUpperCase()}
                </Typography>
              </Box>
              
              <Typography variant="h4" sx={{ 
                mt: 3, 
                fontWeight: 800, 
                color: PINK_THEME, 
                textTransform: 'uppercase',
                letterSpacing: 1.5,
                lineHeight: 1.2
              }}>
                {apellido} {nombre}
              </Typography>
              <Typography variant="subtitle1" sx={{ color: PINK_THEME, fontWeight: 700, mt: 0.5 }}>
                {currentTrip} | {currentYear}
              </Typography>
            </Box>

            <Grid container spacing={4}>
              {/* Left Column */}
              <Grid item xs={12} md={6}>
                <Divider sx={{ mb: 0, borderColor: PINK_THEME, opacity: 0.3 }} />
                <SectionTitle title="DATOS DEL PASAJERO" />
                <InfoRow label="DNI" value={pasajero.dni} />
                <InfoRow label="Fecha de nacimiento" value={pasajero.fecha_nacimiento} />
                <InfoRow label="Sexo" value={pasajero.sexo} />
                <InfoRow label="E-mail" value={pasajero.email} />
                <InfoRow label="Domicilio" value={pasajero.direccion} />
                <InfoRow label="Teléfono Fijo" value={pasajero.telefono_fijo} />
                <InfoRow label="Celular" value={pasajero.celular_personal} />
                <InfoRow label="Provincia" value={(pasajero.ciudad || "") + " " + (pasajero.provincia || "")} />

                <SectionTitle title="DATOS DEL CONTRATO" />
                <InfoRow label="Nombre Padre/Tutor" value={pasajero.datos_del_pagador} />
                <InfoRow label="Apellido Padre/Tutor" value={pasajero.responsable_apellido} />
                <InfoRow label="Dirección Padre/Tutor" value={pasajero.responsable_direccion} />
                <InfoRow label="DNI Padre/Tutor" value={pasajero.dni_responsable} />
                <InfoRow label="Firmó" value={pasajero.firmo_contrato} />
                <InfoRow label="Fecha de firma" value={pasajero.fecha_firma} />
                <InfoRow label="Celular 1" value={pasajero.celular_contacto_1 || (pasajero.telefono_contacto_1 ? `${pasajero.telefono_contacto_1} (${pasajero.dueno_celular_1 || ""})` : "")} />
                <InfoRow label="Celular 2" value={pasajero.celular_contacto_2 || (pasajero.telefono_contacto_2 ? `${pasajero.telefono_contacto_2} (${pasajero.dueno_celular_2 || ""})` : "")} />
              </Grid>

              {/* Right Column */}
              <Grid item xs={12} md={6}>
                <Divider sx={{ mb: 0, borderColor: PINK_THEME, opacity: 0.3 }} />
                <SectionTitle title="DATOS DEL VIAJE" />
                <InfoRow label="Colegio" value={pasajero.colegio} />
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Box sx={{ flex: 1 }}><InfoRow label="Colectivo" value={pasajero.colectivo} /></Box>
                  <Box sx={{ flex: 1 }}><InfoRow label="Emisión" value={pasajero.emision} /></Box>
                </Box>
                
                <Grid container spacing={1} sx={{ mt: 1 }}>
                  <Grid item xs={6}>
                    <Box sx={{ 
                      display: 'flex', 
                      border: '1px solid #eee', 
                      alignItems: 'center',
                      p: 1,
                      bgcolor: 'white'
                    }}>
                      <Box sx={{ width: 30, display: 'flex', justifyContent: 'center' }}>
                         <Typography variant="caption" sx={{ fontWeight: 800, fontSize: '0.55rem', color: PINK_THEME, transform: 'rotate(-90deg)', whiteSpace: 'nowrap' }}>TALLE</Typography>
                      </Box>
                      <Box sx={{ flex: 1, textAlign: 'center' }}>
                        <Checkroom sx={{ color: PINK_THEME, fontSize: 45 }} />
                      </Box>
                      <Box sx={{ bgcolor: VALUE_BG, px: 2, py: 1, borderRadius: 0.5 }}>
                        <Typography variant="body1" sx={{ fontWeight: 900, color: '#333' }}>{pasajero.talle || '-'}</Typography>
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box sx={{ 
                      display: 'flex', 
                      border: '1px solid #eee', 
                      alignItems: 'center',
                      p: 1,
                      bgcolor: 'white'
                    }}>
                      <Box sx={{ width: 30, display: 'flex', justifyContent: 'center' }}>
                         <Typography variant="caption" sx={{ fontWeight: 800, fontSize: '0.55rem', color: PINK_THEME, transform: 'rotate(-90deg)', whiteSpace: 'nowrap' }}>BOLSO</Typography>
                      </Box>
                      <Box sx={{ flex: 1, textAlign: 'center' }}>
                        <Backpack sx={{ color: PINK_THEME, fontSize: 45 }} />
                      </Box>
                      <Box sx={{ bgcolor: VALUE_BG, px: 2, py: 1, borderRadius: 0.5 }}>
                        <Typography variant="body1" sx={{ fontWeight: 900, color: '#333' }}>{pasajero.bolso ? 'SÍ' : 'NO'}</Typography>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>

                <Box sx={{ 
                  mt: 3, 
                  border: '1px solid #eee', 
                  p: 1.5,
                  display: 'flex',
                  alignItems: 'stretch'
                }}>
                  <Box sx={{ width: 30, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography variant="caption" sx={{ fontWeight: 800, fontSize: '0.65rem', color: PINK_THEME, transform: 'rotate(-90deg)', whiteSpace: 'nowrap' }}>DOCUMENTOS</Typography>
                  </Box>
                  <Grid container spacing={1} sx={{ flex: 1 }}>
                    {[
                      { label: "DNI o Pasaporte", field: "dni_o_pasaporte" },
                      { label: "Fotocopia DNI", field: "fotocopia_de_dni" },
                      { label: "Foto Visa", field: "foto_visa" },
                      { label: "Ficha Médica", field: "ficha_medica" },
                      { label: "Partida Nacimiento", field: "partida_de_nacimiento" },
                      { label: "Vacuna", field: "vacuna" },
                      { label: "Permiso", field: "permiso" },
                      { label: "Visa", field: "visa" },
                      { label: "DNI Físico", field: "dni_fisico" },
                      { label: "Pasaporte", field: "pasaporte_europeo" },
                    ].map((doc) => (
                      <Grid item xs={6} key={doc.field}>
                        <Box sx={{ display: 'flex', height: '100%' }}>
                          <Box sx={{ bgcolor: VALUE_BG, px: 1, py: 0.5, flex: 1, textAlign: 'right', mr: 0.5, display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                            <Typography variant="caption" sx={{ fontWeight: 800, fontSize: '0.65rem' }}>{doc.label}</Typography>
                          </Box>
                          <Box sx={{ bgcolor: VALUE_BG, p: 0.5, width: 40, textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Typography variant="caption" sx={{ fontWeight: 800, fontSize: '0.65rem' }}>{pasajero[doc.field] ? 'SI' : 'NO'}</Typography>
                          </Box>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Box>

                <SectionTitle title="DATOS DEL PAGO" />
                <InfoRow label="Monto del producto" value={pasajero.monto_producto} />
                <InfoRow label="Monto suplementario" value={pasajero.monto_suplementario} />
                <InfoRow label="Monto percepción" value={pasajero.monto_percepcion} />
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Box sx={{ flex: 1 }}><InfoRow label="Porcentaje liberado" value={pasajero.porcentaje_liberado ? `${pasajero.porcentaje_liberado}%` : "%"} /></Box>
                  <Box sx={{ flex: 1 }}><InfoRow label="Pagó" value={pasajero.pago_confirmado} /></Box>
                </Box>
                <InfoRow label="Nombre del pagador" value={pasajero.nombre_del_pagador} />
                <InfoRow label="CUIL/CUIT" value={pasajero.cuit_pagador} />
                <InfoRow label="Categoría" value={pasajero.categoria_pago} />
              </Grid>

              {/* Observations */}
              <Grid item xs={12} sx={{ mt: 0 }}>
                <SectionTitle title="OBSERVACIONES" />
                <Box sx={{ 
                  border: `1px solid ${PINK_THEME}`, 
                  p: 2, 
                  minHeight: 100,
                  bgcolor: '#fffafc',
                  borderRadius: 0.5
                }}>
                  <Typography variant="body2" sx={{ color: '#444', lineHeight: 1.6 }}>
                    {pasajero.observaciones || "No se registran observaciones adicionales para este pasajero."}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default PassengerFileModal;
